import { getBinaryId } from '@ctw/shared/api/fhir/binaries';
import type { FHIRModel } from '@ctw/shared/api/fhir/models/fhir-model';
import { searchProvenances } from '@ctw/shared/api/fhir/provenance';
import { getBinaryById } from '@ctw/shared/api/fqs-rest/binaries';
import { notify } from '@ctw/shared/components/toast';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useResourceModal } from '@ctw/shared/context/resource-modal-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import type { Resource } from 'fhir/r4';
import { useCallback } from 'react';

export function useOpenSourceDocument() {
  const telemetry = useTelemetry();
  const { openResourceModal } = useResourceModal({
    layout: {
      mode: 'full',
      overflowBehavior: 'hidden',
      fullbleed: true,
      showCloseButton: false,
    },
  });
  const { ctwFetch, requestContext, graphqlClient } = useCTW();

  const openSourceDocumentById = useCallback(
    async (binaryId: string) => {
      try {
        const binary = await getBinaryById({ binaryId, ctwFetch, requestContext });
        if (binary) {
          openResourceModal({ telemetry, model: binary });
        }
      } catch (error) {
        telemetry.trackError({ message: 'Failed to open source document', error: error as Error });
        notify({ type: 'error', title: 'Unable to load source document' });
      }
    },
    [ctwFetch, openResourceModal, requestContext, telemetry],
  );

  const openSourceDocument = useCallback(
    async (model: FHIRModel<Resource>) => {
      try {
        const provenances = await searchProvenances(telemetry, graphqlClient, [model]);
        const binaryId = getBinaryId(provenances, model.id);
        if (binaryId) {
          await openSourceDocumentById(binaryId);
        }
      } catch (error) {
        telemetry.trackError({
          message: 'Failed to resolve source document',
          error: error as Error,
        });
        notify({ type: 'error', title: 'Unable to load source document' });
      }
    },
    [graphqlClient, openSourceDocumentById, telemetry],
  );

  return { openSourceDocument, openSourceDocumentById };
}
